import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getJson, postJson } from "./client";
import { apiDetail } from "./scenario";

export type FacilityType = "external" | "internal";

/**
 * Change types. Joints deliberately travel through this same propose -> review flow
 * rather than getting their own write endpoint: that is what makes them governed like
 * every other limit change, and it means the approval queue and the audit log need only
 * cosmetic work rather than a parallel implementation.
 */
export type ChangeType = "ADD" | "MODIFY" | "ADD_JOINT" | "MODIFY_JOINT" | "DELETE_JOINT";

export const JOINT_CHANGE_TYPES: ChangeType[] = ["ADD_JOINT", "MODIFY_JOINT", "DELETE_JOINT"];
export const isJointChange = (t?: ChangeType | null) =>
  !!t && JOINT_CHANGE_TYPES.includes(t);

export type LimitStatus = "ACTIVE" | "PENDING" | "RETIRED";

export interface LimitConfigRow {
  limitId: string;
  facilityType: FacilityType;
  facilityCode: string;
  categoryGroup: string;
  limitDesc: string;
  thresholdPct: number;
  capitalCapDollars: number | null;
  effectiveStartDate: string;
  pendingProposalId: string | null;
  /**
   * Internal rows only (external limits have no such concept). PENDING means the limit
   * EXISTS but is switched off — v_prc_concentrations reads `status = 'ACTIVE'`, so it
   * produces no actuals and appears on no page. sql/72_ inserted the 31 external limit
   * categories that way so an internal threshold can be set on each and activated one
   * at a time. Undefined on external rows and in mock mode, which reads as ACTIVE.
   */
  status?: LimitStatus;
  /** The copied rule, read-only. Shown so a category can be judged before a threshold
   *  is committed to it; activating never edits the rule. */
  dimension?: string;
  matchOperator?: string;
  matchValues?: string[];
  measure?: string;
  /** false for the two categories with no derivable rule (titled vehicles,
   *  extended/amended contracts). They are tracked by hand and cannot be activated. */
  computable?: boolean;
}

export interface LimitProposal {
  proposalId: string;
  limitId: string | null;
  facilityType: FacilityType;
  facilityCode: string;
  categoryGroup: string;
  limitDesc: string;
  oldThresholdPct: number | null;
  newThresholdPct: number;
  oldCapitalCapDollars: number | null;
  newCapitalCapDollars: number | null;
  effectiveStartDate: string;
  proposedByRole: string;
  proposedAt: string;
  reason: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "SUPERSEDED";
  reviewedByRole?: string;
  /** When it was approved or rejected (ISO); null while it is still PENDING. */
  reviewedAt?: string | null;
  reviewNotes?: string;
  applyFileDates?: string[] | null;
  // ── Joint proposals. changeType is undefined on every pre-existing row, which
  // correctly reads as a plain limit change in the log.
  changeType?: ChangeType;
  jointId?: string | null;
  jointName?: string;
  jointMemberRefs?: string[] | null;
  /** Snapshotted at proposal time — the audit log wants the names as proposed. */
  memberLimitDescs?: string[];
}

export interface ProposeInput {
  limitId: string | null;
  facilityType: FacilityType;
  facilityCode: string;
  categoryGroup: string;
  limitDesc: string;
  /**
   * For a limit: the proposed threshold. For a JOINT: the joint's alert
   * distance-to-threshold. A joint has no threshold of its own (on the utilisation
   * basis it is always 100), and prc_limit_proposals.new_threshold_pct is NOT NULL, so
   * the column carries a real number a joint genuinely has. This keeps the field
   * required, leaves submitForm untouched, and makes the existing log cell read
   * "— → 90%" honestly.
   */
  newThresholdPct: number;
  newCapitalCapDollars: number | null;
  effectiveStartDate: string;
  reason: string;
  // Optional — external ADD (Navitas taxonomy) / external MODIFY snapshot scoping.
  // The current UI only sends the fields above; the backend accepts these too.
  dimension?: string;
  matchOperator?: string;
  matchValues?: string[];
  measure?: string;
  applyFileDates?: string[] | null;
  // ── Joint proposals
  changeType?: ChangeType;
  jointId?: string | null;
  jointName?: string;
  memberRefs?: string[];
  memberLimitDescs?: string[];
  alertDistToThresholdPct?: number | null;
}

// ── Backend paths ─────────────────────────────────────────────────────────────
const P = "portfolio-risk-concentrations";
// Every one of these is scoped to an investor. Without that, Manage limits showed Wafra's
// external limits under Navitas — the note above the table said there were none while the
// table listed them, which is worse than either answer alone.
//
// The EXTERNAL list is scoped too, even though external limits have no investor column:
// the server answers with an empty list for an investor that funds no external book, so
// the decision stays in one place rather than being re-derived in the browser.
//
// includePending pulls the switched-off internal rows too. They are split out of
// internalLimits below rather than mixed in — see the store's activatableLimits.
const fetchInternal = (investor: string) =>
  getJson<LimitConfigRow[]>(
    `${P}/limits?facility=internal&includePending=true&investor=${investor}`,
  );
const fetchExternal = (investor: string) =>
  getJson<LimitConfigRow[]>(`${P}/limits?facility=external&investor=${investor}`);
const fetchProposals = (investor: string) =>
  getJson<LimitProposal[]>(`${P}/proposals?investor=${investor}`);
const fetchFileDates = () => getJson<string[]>(`${P}/file-dates`);

// Stable empty fallbacks -- `?? []` would create a new array every render while a query's
// `data` is still undefined, which is exactly the reference churn the memoization below
// exists to avoid. One shared instance per shape is enough since none of these are ever
// mutated, only read.
const EMPTY_LIMITS: LimitConfigRow[] = [];
const EMPTY_PROPOSALS: LimitProposal[] = [];
const EMPTY_FILE_DATES: string[] = [];

// ═══════════════════════════════════════════════════════════════════════════
//  Store hook — same interface the pages consume, backed by the live API.
// ═══════════════════════════════════════════════════════════════════════════
export function useLimitConfigStore(investor = "WAFRA") {
  const qc = useQueryClient();
  // investor is part of every key: two investors' limit sets are different data, not the
  // same data filtered, so they must not share a cache entry.
  // placeholderData keeps showing the PREVIOUS investor's rows while the new investor's
  // fetch is in flight, instead of each query dropping to `undefined` the instant the key
  // changes. Without it, switching investor forces internalLimits/externalLimits/proposals
  // through an empty-list frame before the real data lands -- three independent queries
  // resolving at three different times, each one its own render with its own downstream
  // recompute of comparisonRows/jointRows/resolvableRefs (all of Manage Limits' heavier
  // memo work) and a fresh externalLimits/internalLimits reference into DefineJointDialog
  // (permanently mounted, its own useJointCandidates call). That render burst, not any one
  // query being slow, is what a switch-investor freeze traced back to.
  const internalQ = useQuery({
    queryKey: ["prc-limits", "internal", investor],
    queryFn: () => fetchInternal(investor),
    placeholderData: keepPreviousData,
  });
  const externalQ = useQuery({
    queryKey: ["prc-limits", "external", investor],
    queryFn: () => fetchExternal(investor),
    placeholderData: keepPreviousData,
  });
  const proposalsQ = useQuery({
    queryKey: ["prc-proposals", investor],
    queryFn: () => fetchProposals(investor),
    placeholderData: keepPreviousData,
  });
  const fileDatesQ = useQuery({ queryKey: ["prc-file-dates"], queryFn: fetchFileDates });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["prc-limits"] });
    qc.invalidateQueries({ queryKey: ["prc-proposals"] });
    // Approving a proposal mutates the live data the Monitor/Capacity surfaces read.
    qc.invalidateQueries({ queryKey: ["prc-concentration"] });
    qc.invalidateQueries({ queryKey: ["prc-capacity"] });
    // ...and, for a joint proposal, everything the Joints surfaces read.
    qc.invalidateQueries({ queryKey: ["prc-joints"] });
    qc.invalidateQueries({ queryKey: ["prc-joint-defs"] });
    qc.invalidateQueries({ queryKey: ["prc-joint-candidates"] });
  };

  // The internal fetch now carries PENDING rows too, so it is split here rather than at
  // every call site. internalLimits stays LIVE-ONLY: it feeds the Approved View table and
  // DefineJointDialog's member picker, and a PENDING limit has no headroom — selecting one
  // as a joint member would drop it out of the min() that defines joint headroom, which
  // OVERSTATES capacity. That is the one direction a risk figure must never fail in.
  //
  // Memoized on the query's own `data` reference, not recomputed on every render: every
  // consumer (ManageLimitsPage's comparisonRows/jointRows/resolvableRefs memos,
  // DefineJointDialog's allLimits) depends on these by reference, so a `[]` or `.filter()`
  // result rebuilt fresh each render defeats every one of those memos silently -- they still
  // "work", they just recompute unconditionally, which is real, avoidable cost on a page with
  // this many derived tables.
  const allInternal = internalQ.data ?? EMPTY_LIMITS;
  const internalLimits = useMemo(
    () => allInternal.filter((r) => (r.status ?? "ACTIVE") === "ACTIVE"),
    [allInternal],
  );
  const externalLimits = externalQ.data ?? EMPTY_LIMITS;
  const proposals = proposalsQ.data ?? EMPTY_PROPOSALS;
  const fileDates = fileDatesQ.data ?? EMPTY_FILE_DATES;

  // A failed call carries the server's own reason ("... already has a proposal awaiting review",
  // "You raised this proposal, ...") in the response's `detail`; axios' own message is only
  // "Request failed with status code 409".
  const propose = async (input: ProposeInput) => {
    try {
      // Stamped here rather than at each call site: every proposal belongs to the investor
      // whose appetite was on screen when it was raised, and forgetting it on one form would
      // silently file the change under the wrong investor.
      await postJson(`${P}/proposals`, { investor, ...input });
    } catch (err) {
      throw new Error(apiDetail(err, "Failed to submit proposal."));
    }
    invalidate();
  };
  // The review endpoint answers a refused or failed approval with HTTP 200 and {ok: false, error}
  // ("already APPROVED", or why the change could not be applied), so the body has to be read:
  // treating the 200 as success cleared the notes and left the proposal sitting in the queue with
  // no message. Every other proposal screen here (capital, investors, scenario) does the same check.
  const review = async (proposalId: string, decision: "APPROVED" | "REJECTED", notes: string) => {
    try {
      const res = await postJson<{ ok: boolean; error?: string }>(
        `${P}/proposals/${encodeURIComponent(proposalId)}/review`,
        { decision, notes },
      );
      if (!res.ok) throw new Error(res.error || "The review could not be applied.");
    } catch (err) {
      // the queue may be stale (someone else decided it first): refresh it, then show why
      invalidate();
      throw new Error(apiDetail(err, "Review failed."));
    }
    invalidate();
  };

  // Limit categories that exist but are switched off — the 31 copied from the external
  // books by sql/72_. The two non-computable ones (titled vehicles, extended/amended
  // contracts) are excluded: they have no derivable rule, so activating one would produce
  // no actuals and the view would INNER JOIN it away with nothing saying why.
  const activatableLimits = useMemo(
    () => allInternal.filter((r) => r.status === "PENDING" && r.computable !== false),
    [allInternal],
  );
  const pendingProposals = useMemo(
    () => proposals.filter((p) => p.status === "PENDING"),
    [proposals],
  );

  return {
    externalLimits,
    internalLimits,
    activatableLimits,
    fileDates,
    limitsFor: (type: FacilityType) => (type === "external" ? externalLimits : internalLimits),
    proposals,
    pendingProposals,
    propose,
    review,
  };
}
